import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { updateUserQuota } from "../../api/admin.api";
import { Button } from "../ui/Button";
import { formatBytes } from "../../utils/formatBytes";

const PRESETS = [
  { label: "500 MB", bytes: "524288000" },
  { label: "1 GB",   bytes: "1073741824" },
  { label: "5 GB",   bytes: "5368709120" },
  { label: "10 GB",  bytes: "10737418240" },
  { label: "50 GB",  bytes: "53687091200" },
  { label: "100 GB", bytes: "107374182400" },
  { label: "1 TB",   bytes: "1099511627776" },
];

const UNIT_MULTIPLIERS: Record<string, bigint> = {
  MB: 1048576n,
  GB: 1073741824n,
  TB: 1099511627776n,
};

interface QuotaEditorProps {
  userId: string;
  currentQuotaBytes: string;
  usedBytes: string;
  onSaved: () => void;
  onCancel: () => void;
}

export function QuotaEditor({
  userId,
  currentQuotaBytes,
  usedBytes,
  onSaved,
  onCancel,
}: QuotaEditorProps) {
  const [selected, setSelected] = useState(currentQuotaBytes);
  const [customValue, setCustomValue] = useState("");
  const [unit, setUnit] = useState("GB");

  const mutation = useMutation({
    mutationFn: () => updateUserQuota(userId, selected),
    onSuccess: onSaved,
  });

  // Convert custom input → bytes string
  const applyCustom = () => {
    const n = parseFloat(customValue);
    if (!isFinite(n) || n <= 0) return;
    const bytes = (BigInt(Math.round(n * 1000)) * UNIT_MULTIPLIERS[unit]!) / 1000n;
    setSelected(bytes.toString());
    setCustomValue("");
  };

  const selectedBytes = BigInt(selected);
  const usedBytesN = BigInt(usedBytes);
  const isBelowUsed = selectedBytes < usedBytesN;
  const isUnchanged = selected === currentQuotaBytes;

  return (
    <div className="space-y-3 min-w-[220px]">
      {/* Current → New */}
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <span>{formatBytes(currentQuotaBytes)}</span>
        <svg className="h-3 w-3" viewBox="0 0 16 16" fill="currentColor">
          <path fillRule="evenodd" d="M1 8a.5.5 0 01.5-.5h11.793l-3.147-3.146a.5.5 0 01.708-.708l4 4a.5.5 0 010 .708l-4 4a.5.5 0 01-.708-.708L13.293 8.5H1.5A.5.5 0 011 8z" />
        </svg>
        <span className={`font-semibold ${isBelowUsed ? "text-red-400" : "text-brand-400"}`}>
          {formatBytes(selected)}
        </span>
      </div>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.bytes}
            onClick={() => setSelected(p.bytes)}
            className={`rounded px-2 py-0.5 text-xs transition-colors ${
              selected === p.bytes
                ? "bg-brand-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom input */}
      <div className="flex items-center gap-1">
        <input
          type="number"
          min="1"
          step="any"
          placeholder="Custom"
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyCustom()}
          className="w-24 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 focus:border-brand-500 focus:outline-none"
        />
        <select
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          className="rounded border border-gray-600 bg-gray-800 px-1 py-1 text-xs text-gray-100 focus:border-brand-500 focus:outline-none"
        >
          <option value="MB">MB</option>
          <option value="GB">GB</option>
          <option value="TB">TB</option>
        </select>
        <button
          onClick={applyCustom}
          disabled={!customValue}
          className="rounded bg-gray-700 px-2 py-1 text-xs text-gray-300 hover:bg-gray-600 disabled:opacity-40"
        >
          Set
        </button>
      </div>

      {/* Warning if new quota is below current usage */}
      {isBelowUsed && (
        <p className="text-xs text-red-400">
          Warning: quota is below current usage ({formatBytes(usedBytes)})
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          loading={mutation.isPending}
          onClick={() => mutation.mutate()}
          disabled={isUnchanged || isBelowUsed}
        >
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
